/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { ITooltipStyles } from '@fluentui/react/lib/Tooltip';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import { default as React } from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { CopyButton } from 'src/core/components/CopyButton';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { Page } from 'src/core/components/Page';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { UnsavedChangesPrompt } from 'src/core/components/UnsavedChangesPrompt';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { logButtonClick, logTextEntry } from 'src/core/services/telemetry/eventsNamesTemplate';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { FormValidators } from 'src/core/utils/formValidators';
import { globalMessages } from 'src/core/utils/globalMessages';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { TooltipProps } from 'src/modules/labeling/components/Tooltip';
import { QnaMakerProjectStore } from 'src/qnaMaker/core/stores/QnaMakerProjectStore';
import { QnaMakerProjectDto } from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/QnaMakerProjectCreationModal';

type QnaMakerProjectSettingsDto = Pick<QnaMakerProjectStore, 'description' | 'defaultAnswer'>;

const messages = defineMessages({
    saveButton: { id: 'qna.qnaProjectSettingsPage.saveButton', defaultMessage: 'Save' },
    enabledToggle: { id: 'qna.qnaProjectSettingsPage.enabledToggle', defaultMessage: 'Enabled' },
    nameLabel: { id: 'qna.qnaProjectSettingsPage.projectTitle', defaultMessage: 'Project Name' },
    pageTitle: { id: 'qna.qnaProjectSettingsPage.pageTitle', defaultMessage: 'Project settings' },
    languageTitle: { id: 'qna.qnaProjectSettingsPage.languageTitle', defaultMessage: 'Language' },
    notEnabledToggle: { id: 'qna.qnaProjectSettingsPage.notEnabledToggle', defaultMessage: 'Not enabled' },
    descriptionLabel: { id: 'qna.qnaProjectSettingsPage.descriptionLabel', defaultMessage: 'Project description' },
    nameSectionTitle: { id: 'qna.qnaProjectSettingsPage.nameSectionTitle', defaultMessage: 'Name and description' },
    goToOrchestration: { id: 'qna.qnaProjectSettingsPage.goToOrchestration', defaultMessage: 'Go to orchestration' },
    orchestrationTitle: { id: 'qna.qnaProjectSettingsPage.orchestrationTitle', defaultMessage: 'Orchestrate project' },
    multiLingualTitle: { id: 'qna.qnaProjectSettingsPage.multiLingualTitle', defaultMessage: 'Multiple language enabled?' },
    primaryLanguageTitle: { id: 'qna.qnaProjectSettingsPage.primaryLanguageTitle', defaultMessage: 'Text primary language:' },
    defaultAnswerLabel: { id: 'qna.qnaProjectSettingsPage.defaultAnswerLabel', defaultMessage: 'Default answer when no result found' },
    orchestrationDescription: {
        id: 'qna.qnaProjectSettingsPage.orchestrationDescription',
        defaultMessage:
            'Orchestration workflow allows you to chain multiple projects together in Language, such as orchestrating between other Conversation projects, LUIS, and QnA Maker KBs'
    },
    multiLingualLabel: {
        id: 'qna.qnaProjectSettingsPage.multiLingualLabel',
        defaultMessage: 'Once multiple languages have been enabled, they cannot be disabled'
    }
});

const isFormDirty = (currentProject: QnaMakerProjectStore, currentForm: QnaMakerProjectSettingsDto) =>
    currentForm.description.trim() !== currentProject.description.trim() ||
    currentForm.defaultAnswer.trim() !== currentProject.defaultAnswer.trim();

const areFieldValuesValid = (currentForm: QnaMakerProjectSettingsDto) =>
    !(currentForm.description.length > 0 && !currentForm.description.trim().length) && currentForm.defaultAnswer.trim().length;

const rbacTooltipStyles = classNamesFunction<TooltipProps, ITooltipStyles>()({ root: { width: '400px' } });

export const QnaMakerProjectSettingsPage = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { rootStore, dispatcher } = useGlobalState();
    const updateTracker = useProgressTracker();

    const currentProject = rootStore.currentQnaMakerProjectStore;

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const { formState, formStateDto, updateFormState } = useFormState<QnaMakerProjectSettingsDto>({
        description: [currentProject.description.trim()],
        defaultAnswer: [currentProject.defaultAnswer.trim(), FormValidators.createRequiredValidator()]
    });

    const supportedLanguageCode = currentProject.language;
    const projectLanguage = LuisConstants.QnaMaker.SupportedProjectLangues.find(lang => lang.code === supportedLanguageCode).name;

    const updateQnaMakerProject = () => {
        const { description, defaultAnswer } = formStateDto;

        if (defaultAnswer.trim() !== currentProject.defaultAnswer.trim()) {
            logTextEntry(serviceName, 'Settings', 'DefaultAnswer', currentProject?.id);
        }

        const projectMetaData: QnaMakerProjectDto = {
            name: currentProject.name,
            description: description.trim(),
            language: currentProject.language,
            defaultAnswer: defaultAnswer.trim(),
            multilingualResource: currentProject.multilingualResource
        };

        dispatcher.dispatch('updateQnaMakerProject', {
            projectMetaData,
            toastData: {},
            tracker: updateTracker
        });
    };

    const resetForm = () => {
        updateFormState({
            description: currentProject.description.trim(),
            defaultAnswer: currentProject.defaultAnswer.trim()
        });
    };

    const isSaveButtonDisabled: boolean =
        updateTracker.isRunning ||
        !(formState.form.valid && isFormDirty(currentProject, formStateDto) && areFieldValuesValid(formStateDto));

    const isCancelButtonDisabled: boolean = updateTracker.isRunning || !isFormDirty(currentProject, formStateDto);

    const items: ICommandBarItemProps[] = [
        {
            key: 'save',
            onClick: updateQnaMakerProject,
            disabled: isSaveButtonDisabled,
            iconProps: { iconName: 'Save' },
            text: localize(messages.saveButton)
        },
        {
            key: 'cancel',
            onClick: resetForm,
            disabled: isCancelButtonDisabled,
            iconProps: { iconName: 'Cancel' },
            text: localize(globalMessages.cancel)
        }
    ];

    const renderTitles = (title: string) => (
        <LuisText variant="mediumPlus" fontWeight={600}>
            {title}
        </LuisText>
    );

    const renderLabeledText = (label: string, text: string | React.ReactNode) => (
        <Stack verticalAlign="center">
            <Label styles={{ root: { width: '250px' } }}>{label}</Label>
            <LuisText>{text}</LuisText>
        </Stack>
    );

    return (
        <Page size="container-fluid" pageName="QnaMakerProjectSettingsPage" title={localize(messages.pageTitle)}>
            <EditableTitle title={localize(messages.pageTitle)} />
            <LuisCommandBar items={items} />

            <UnsavedChangesPrompt
                isPageDirty={isFormDirty(currentProject, formStateDto)}
                promptPath={routeTable.qnaMaker.settingsPage(currentProject.name)}
            />

            <Stack tokens={{ childrenGap: 24 }} styles={{ root: { marginTop: '20px' } }}>
                <Stack tokens={{ childrenGap: 12 }}>
                    {renderTitles(localize(messages.nameSectionTitle))}

                    <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 10 }}>
                        <LuisTextField
                            disabled
                            styles={{ root: { width: 400 } }}
                            value={currentProject.name}
                            label={localize(messages.nameLabel)}
                        />

                        <CopyButton textToCopy={currentProject.name} label={localize(messages.nameLabel)} />
                    </Stack>

                    <RBACTooltip
                        hidden={canWrite}
                        styles={rbacTooltipStyles}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="projectDescriptionFieldLanguageToolTip"
                        componentSurroundedByToolTip={
                            <LuisTextField
                                multiline
                                disabled={!canWrite}
                                styles={{ root: { width: 400 } }}
                                dir={currentProject.languageDirection}
                                value={formState.fields.description.value}
                                label={localize(messages.descriptionLabel)}
                                onChange={(_, value) => updateFormState({ description: value })}
                            />
                        }
                    />
                </Stack>

                <Stack tokens={{ childrenGap: 12 }}>
                    {renderTitles(localize(messages.languageTitle))}
                    {renderLabeledText(localize(messages.primaryLanguageTitle), projectLanguage)}
                    {renderLabeledText(localize(messages.multiLingualTitle), localize(messages.multiLingualLabel))}
                    <Toggle
                        disabled
                        styles={{ root: { marginBottom: '0' } }}
                        checked={currentProject.multilingualResource}
                        offText={localize(messages.notEnabledToggle)}
                        ariaLabel={localize(messages.multiLingualTitle)}
                    />
                </Stack>

                <RBACTooltip
                    hidden={canWrite}
                    styles={rbacTooltipStyles}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="projectDefaultAnswerFieldLanguageToolTip"
                    componentSurroundedByToolTip={
                        <LuisTextField
                            required
                            disabled={!canWrite}
                            styles={{ root: { width: 400 } }}
                            dir={currentProject.languageDirection}
                            value={formState.fields.defaultAnswer.value}
                            label={localize(messages.defaultAnswerLabel)}
                            onChange={(_, value) => updateFormState({ defaultAnswer: value })}
                        />
                    }
                />

                <Stack tokens={{ childrenGap: 15 }}>
                    {renderTitles(localize(messages.orchestrationTitle))}
                    <LuisText>{localize(messages.orchestrationDescription)}</LuisText>
                    <DefaultButton
                        text={localize(messages.goToOrchestration)}
                        styles={{ root: { alignSelf: 'flex-start', marginBottom: 25 } }}
                        onClick={() => {
                            logButtonClick(serviceName, 'Settings', 'GoToOrchestration', null, currentProject.id);
                            history.push(routeTable.orchestration.projectsPage());
                        }}
                    />
                </Stack>
            </Stack>
        </Page>
    );
});
